import { Component, createRef } from "react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { ServerRow, ServerInputRow, ProjectRow, EnvironmentRow, ServerCloud } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { InfraLayout } from '../InfraLayout';
import { InfraEntities } from "../InfraEntities";
import { InfraRouterProps } from "../InfraRouterProps";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";

interface ServerEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface ServerEditPageState
{
    selected_project_id?: string;
}

export default class ServerEditPage extends Component<ServerEditPageProps, ServerEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_Cloud = createRef<NSBoxEnum<ServerCloud>>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: ServerEditPageProps)
    {
        super(props);
        this.state = {}
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InfraLayout<ServerRow, ServerInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.server(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ServerInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let cloud = this.NSBoxEnum_Cloud.current?.getValueEnumOne(ServerCloud, ServerCloud.AWS) ?? ServerCloud.AWS;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            workspace_id: "",
                                            name,
                                            cloud,
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            description,
                                            server_category: [],
                                            server_field: [],
                                            server_tag: [],
                                        };
                                    },
                                    setRow: (row: ServerRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Cloud.current?.setValueOne(row.cloud);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                            }),
                                            () => this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id)
                                        );
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                        // info={{ link: "https://namirasoft.com/infra/console-guide/server/#field-name" }}
                        />
                        <NSBoxEnum<ServerCloud>
                            ref={this.NSBoxEnum_Cloud}
                            title="Cloud"
                            required
                            multiple={false}
                            getEnumObject={() => ServerCloud}
                            getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/server/#field-cloud" }}
                        />
                    </NSRow>
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () => this.NSBoxEntity_EnvironmentID.current?.reload(),
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    {
                        this.state.selected_project_id &&
                        <NSBoxEntity<EnvironmentRow>
                            ref={this.NSBoxEntity_EnvironmentID}
                            title="Environment"
                            required
                            multiple={false}
                            table_name="environment"
                            list={async (filters, page, size, sorts) =>
                            {
                                if (!filters)
                                    filters = [];

                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id!
                                ));

                                return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                            }}
                            getValue={(item) => item.id}
                            getTitle={(item) => item.name}
                            getSort={() => null}
                            style={{ width: NSBox.width.double }}
                        />
                    }
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/server/#field-log-group" }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required
                        title="Description"
                        min_length={50}
                        // info={{ link: "https://namirasoft.com/infra/console-guide/server/#field-description" }}
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </InfraLayout>
        );
    }
}